import { Copy, Link2, MessageSquare, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  buildShareUrlFromToken,
  type ShareSummary,
  useCreateShare,
  useFileShares,
} from "@/hooks/use-document-shares";
import { useOrgFiles } from "@/hooks/use-org-files";
import { ApiError } from "@/lib/api";

/**
 * Reusable Create-share dialog.
 *
 * - When `lockedFileId` is provided, the file picker is hidden, existing
 *   active shares for that file are listed at the top, and a new share can
 *   be created below. Use from the All Documents / Project Documents Share
 *   action.
 * - When `lockedFileId` is omitted, a project-scoped file picker is shown.
 *   Use from the standalone Document Shares page.
 */
interface CreateShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  lockedFileId?: string;
  lockedFileName?: string;
}

export function CreateShareDialog({
  open,
  onOpenChange,
  projectId,
  lockedFileId,
  lockedFileName,
}: CreateShareDialogProps) {
  const showPicker = !lockedFileId;
  const filesQuery = useOrgFiles({ projectIds: [projectId], limit: 100 });
  const existingShares = useFileShares(showPicker ? null : (lockedFileId ?? null));
  const create = useCreateShare(projectId);

  const [fileId, setFileId] = useState<string>(lockedFileId ?? "");
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [emailGate, setEmailGate] = useState(true);
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const [confirmRestricted, setConfirmRestricted] = useState(false);
  const [restrictedReason, setRestrictedReason] = useState("");
  const [restrictedFolderClass, setRestrictedFolderClass] = useState<string | null>(null);
  const [hardBlockClass, setHardBlockClass] = useState<string | null>(null);

  const files = useMemo(
    () => (showPicker ? (filesQuery.data?.pages.flatMap((p) => p.data) ?? []) : []),
    [filesQuery.data, showPicker],
  );

  function reset() {
    setFileId(lockedFileId ?? "");
    setExpiresInDays(14);
    setEmailGate(true);
    setPin("");
    setMessage("");
    setConfirmRestricted(false);
    setRestrictedReason("");
    setRestrictedFolderClass(null);
    setHardBlockClass(null);
  }

  function handleClose(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  async function handleCreate() {
    setHardBlockClass(null);
    if (!fileId) {
      toast.error("Pick a file first");
      return;
    }
    try {
      await create.mutateAsync({
        fileId,
        expiresInDays,
        emailGateEnabled: emailGate,
        pin: pin || undefined,
        message: message || undefined,
        confirmRestricted: confirmRestricted || undefined,
        restrictedReason: restrictedReason || undefined,
      });
      // Clear the new-share inputs so the form is ready for another share.
      // The existing-shares panel above will refresh via cache invalidation
      // and now include the share that was just created — including its
      // rebuildable URL.
      setExpiresInDays(14);
      setEmailGate(true);
      setPin("");
      setMessage("");
      setConfirmRestricted(false);
      setRestrictedReason("");
      setRestrictedFolderClass(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as {
          error?: string;
          folderClass?: string;
          documentClass?: string;
        } | null;
        if (body?.error === "restricted_class_unconfirmed") {
          setRestrictedFolderClass(body.folderClass ?? "restricted");
          return;
        }
        if (body?.error === "hard_blocked") {
          setHardBlockClass(body.documentClass ?? "hard_blocked");
          return;
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Share document</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* File context (locked) */}
          {!showPicker && (
            <div className="rounded border border-gray-200 bg-gray-50 p-2 text-sm">
              <span className="text-gray-500">Sharing: </span>
              <span className="font-medium text-gray-900">{lockedFileName ?? lockedFileId}</span>
            </div>
          )}

          {/* Existing active shares for this file */}
          {!showPicker && existingShares.data && existingShares.data.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Active links
              </p>
              <div className="space-y-2">
                {existingShares.data.map((s) => (
                  <ExistingShareRow key={s.id} share={s} />
                ))}
              </div>
            </div>
          )}

          {/* New share form */}
          <div className="space-y-4 rounded border border-gray-200 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Create a new link
            </p>

            {showPicker && (
              <div>
                <Label htmlFor="file-pick">File</Label>
                <Select value={fileId} onValueChange={setFileId}>
                  <SelectTrigger id="file-pick">
                    <SelectValue placeholder="Pick a file" />
                  </SelectTrigger>
                  <SelectContent>
                    {files.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.fileName}
                        {f.folderPath ? ` (${f.folderPath})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="expires">Expires in (days)</Label>
                <Input
                  id="expires"
                  type="number"
                  min={1}
                  max={90}
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                />
                <p className="mt-1 text-xs text-gray-500">Default 14, max 90.</p>
              </div>
              <div>
                <Label htmlFor="pin">PIN (optional)</Label>
                <Input
                  id="pin"
                  type="text"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Leave blank for no PIN"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="email-gate"
                type="checkbox"
                checked={emailGate}
                onChange={(e) => setEmailGate(e.target.checked)}
              />
              <Label htmlFor="email-gate" className="text-sm font-normal">
                Require email before viewing
              </Label>
            </div>

            <div>
              <Label htmlFor="message">Message to recipient (optional)</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
            </div>

            {restrictedFolderClass && (
              <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm">
                <p className="font-medium text-yellow-900">
                  Restricted folder ({restrictedFolderClass})
                </p>
                <p className="mt-1 text-yellow-800">
                  This file is in a restricted folder class. Confirm and provide a reason to
                  proceed.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="confirm-restricted"
                    type="checkbox"
                    checked={confirmRestricted}
                    onChange={(e) => setConfirmRestricted(e.target.checked)}
                  />
                  <Label htmlFor="confirm-restricted" className="text-sm font-normal">
                    I confirm this share is intentional
                  </Label>
                </div>
                <Textarea
                  className="mt-2"
                  placeholder="Reason (logged for audit)"
                  rows={2}
                  value={restrictedReason}
                  onChange={(e) => setRestrictedReason(e.target.value)}
                />
              </div>
            )}

            {hardBlockClass && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                This document is{" "}
                <strong>
                  {hardBlockClass.replace("_", " ").replace(/^./, (c) => c.toUpperCase())}
                </strong>{" "}
                and cannot be shared via public link. Send a portal invite instead.
              </div>
            )}

            <Button
              onClick={handleCreate}
              disabled={create.isPending || !fileId || !!hardBlockClass}
              className="w-full"
            >
              {create.isPending ? "Creating…" : "Create share"}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExistingShareRow({ share }: { share: ShareSummary }) {
  const url = buildShareUrlFromToken(share.rawToken, share.mode);
  const expires = new Date(share.expiresAt);
  const expired = expires.getTime() < Date.now();
  return (
    <div className="rounded border border-gray-200 bg-white p-2">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 shrink-0 text-gray-500" />
        {url ? (
          <code className="flex-1 break-all text-xs">{url}</code>
        ) : (
          <span className="flex-1 text-xs text-gray-400">
            URL unavailable for legacy share — create a new one.
          </span>
        )}
        {url && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(url);
              toast.success("Copied");
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
        {url && (
          <Button asChild size="sm" variant="outline">
            <a href={url} target="_blank" rel="noopener noreferrer">
              Open
            </a>
          </Button>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span>
          {expired ? "Expired" : "Expires"} {expires.toLocaleDateString()}
        </span>
        {share.emailGateEnabled && (
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" />
            Email gate
          </span>
        )}
        {share.hasPin && (
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" />
            PIN
          </span>
        )}
        <span>
          {share.accessCount} access{share.accessCount === 1 ? "" : "es"}
        </span>
        {share.message && (
          <span className="inline-flex items-start gap-1" title={share.message}>
            <MessageSquare className="h-3 w-3 mt-0.5" />
            <span className="line-clamp-1 max-w-xs">{share.message}</span>
          </span>
        )}
      </div>
    </div>
  );
}
